/**
 * The group position.
 *
 * ═══════════════════════════════════════════════════════════════════
 * A SUM OF ENTITIES, AND IT SAYS SO.
 *
 * This is not a separate ledger. It adds together the entities flagged to
 * consolidate, from their own posted transactions, and states that no
 * consolidation adjustment or elimination of inter-entity trading has been
 * applied. Presenting a sum as though it were a consolidated statutory
 * position would be a lie that only shows up at audit — so the basis of
 * preparation is the first thing on the page, not a footnote under a
 * nine-column table where nobody reads it.
 *
 * An entity can be excluded from consolidation without being excluded from the
 * system — which is exactly what a group needs when it runs a separate trading
 * company or a shared service for somebody else. Excluded entities are shown,
 * and shown as excluded, rather than quietly dropped.
 *
 * Each entity resolves its OWN financial year, so a group is not guaranteed to
 * be adding up one period. Where the years agree the page names the year; where
 * they do not it says so and shows the year per entity, because a total that
 * silently spans two years is the kind of figure a Finance Director signs and
 * then has to withdraw.
 *
 * Group reporting is a licensed feature. Where it is switched off this screen
 * says so plainly and asks the server for nothing: a page reachable by a
 * bookmark, a back button or a typed address must never greet a customer with
 * the failure of a request it should never have made.
 * ═══════════════════════════════════════════════════════════════════
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useAsync, useMutation } from '../lib/useAsync';
import { useSession } from '../lib/session';
import { lower, useTenant, useTerms } from '../lib/tenant';
import { money, percent } from '../lib/format';
import {
  Alert,
  AsyncPanel,
  Badge,
  Card,
  DataTable,
  EmptyState,
  ErrorNotice,
  Money,
  PageHeader,
  Progress,
  Stat,
  type Column,
} from '../components/ui';

interface EntityPosition {
  entityId: string;
  entityCode: string;
  entityName: string;
  entityType: string;
  isCentral: boolean;
  consolidates: boolean;
  financialYear: string;
  budgetVersion: string | null;
  incomeMinor: number;
  expenditureMinor: number;
  surplusMinor: number;
  budgetIncomeMinor: number;
  budgetExpenditureMinor: number;
  budgetSurplusMinor: number;
  commitmentMinor: number;
  creditorsMinor: number;
  debtorsMinor: number;
  cashMinor: number;
}

interface ConsolidationResponse {
  entities: EntityPosition[];
  consolidated: {
    entityCount: number;
    excludedCount: number;
    incomeMinor: number;
    expenditureMinor: number;
    surplusMinor: number;
    budgetIncomeMinor: number;
    budgetExpenditureMinor: number;
    budgetSurplusMinor: number;
    commitmentMinor: number;
    creditorsMinor: number;
    debtorsMinor: number;
    cashMinor: number;
  };
  basis: string;
}

export function ConsolidationPage() {
  const t = useTerms();
  const { featureEnabled } = useTenant();
  const enabled = featureEnabled('consolidatedDashboard');

  return (
    <>
      <PageHeader
        title={`${t.consolidated} position`}
        subtitle={
          enabled
            ? `Every ${lower(t.entity)} side by side, from its own posted transactions.`
            : `A single view across every ${lower(t.entity)} in the ${lower(t.organisation)}.`
        }
      />

      {enabled ? (
        <GroupPosition />
      ) : (
        <Card>
          <EmptyState title={`${t.consolidated} reporting is not switched on`}>
            <p>
              This {lower(t.organisation)} is configured to run each {lower(t.entity)} on its own, so there is no
              {' '}
              {lower(t.consolidated)} position to show. Nothing is wrong and nothing is missing.
            </p>
            <p style={{ marginTop: '0.5rem' }}>
              If you need the {lower(t.entity)} comparison, your finance system administrator can enable{' '}
              {lower(t.consolidated)} reporting in the organisation settings.
            </p>
          </EmptyState>
        </Card>
      )}
    </>
  );
}

/**
 * The body of the screen, separated from the licensing gate above so that the
 * request is never made — not made and discarded — when the feature is off.
 */
function GroupPosition() {
  const t = useTerms();
  const { user, switchEntity } = useSession();
  const navigate = useNavigate();
  const data = useAsync(() => api.get<ConsolidationResponse>('/platform/consolidation'), []);

  // Which entity a switch is in flight for, so the row that was pressed is the
  // row that shows it rather than the whole table going busy.
  const [pendingId, setPendingId] = useState<string | null>(null);
  const move = useMutation(async (entity: EntityPosition) => {
    await switchEntity(entity.entityId);
    return entity.entityName;
  });

  const moveInto = async (entity: EntityPosition): Promise<void> => {
    setPendingId(entity.entityId);
    const moved = await move.run(entity);
    setPendingId(null);
    // Switching re-scopes the whole session, so the user has to land somewhere
    // that shows it. Staying on this page would look like the click did nothing.
    if (moved !== null) navigate('/');
  };

  // The group lists every entity in the organisation; this user may hold a role
  // in only some of them. A switch into the others would be refused by the
  // server, so the control is not offered for them.
  const reachable = new Set((user?.entities ?? []).map((e) => e.entityId));

  return (
    <AsyncPanel state={data}>
      {(view) => {
        const consolidating = view.entities.filter((e) => e.consolidates || e.isCentral);
        const years = Array.from(new Set(consolidating.map((e) => e.financialYear)));
        const commonYear = years.length === 1 ? (years[0] ?? null) : null;
        // Only worth a column of its own when the entities disagree; when they
        // agree, one line in the card subtitle says it once for the whole table.
        const showYear = commonYear === null && years.length > 1;

        const hasGroupBudget =
          view.consolidated.budgetIncomeMinor !== 0 || view.consolidated.budgetExpenditureMinor !== 0;
        const spendAgainstBudget =
          view.consolidated.budgetExpenditureMinor > 0
            ? (view.consolidated.expenditureMinor / view.consolidated.budgetExpenditureMinor) * 100
            : 0;

        const yearNote = commonYear
          ? ` · Financial year ${commonYear}`
          : showYear
            ? ` · Financial years differ between ${lower(t.entityPlural)}, so the total spans more than one year`
            : '';

        const entityColumn: Column<EntityPosition> = {
          key: 'entity',
          header: t.entity,
          // Wide enough for a real legal name; without it the name and its
          // switch arrow break across two lines on every row.
          width: '17rem',
          sortValue: (e) => e.entityCode,
          render: (e) => {
            const here = user?.entityId === e.entityId;
            const canWorkIn = reachable.has(e.entityId);
            const excluded = !e.consolidates && !e.isCentral;
            // Rows with nothing to flag get no second line at all, so the table
            // keeps an even rhythm instead of an empty gap under most names.
            const hasFlags = here || e.isCentral || excluded || !canWorkIn;
            return (
              <>
                <span className="mono smaller">{e.entityCode}</span>{' '}
                {here ? (
                  <strong>{e.entityName}</strong>
                ) : canWorkIn ? (
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    onClick={() => void moveInto(e)}
                    disabled={move.busy}
                    title={`Work in ${e.entityName}`}
                    aria-label={`Work in ${e.entityName}`}
                  >
                    {pendingId === e.entityId ? 'Switching…' : `${e.entityName} →`}
                  </button>
                ) : (
                  <span>{e.entityName}</span>
                )}
                {hasFlags ? (
                  <div className="row" style={{ gap: '0.35rem', marginTop: '0.25rem' }}>
                    {here ? <Badge tone="info">You are working here</Badge> : null}
                    {e.isCentral ? <Badge tone="brand">{t.centralUnit}</Badge> : null}
                    {excluded ? <Badge tone="warn">Excluded from the total</Badge> : null}
                    {!here && !canWorkIn ? (
                      <span className="smaller muted">You do not have access to this {lower(t.entity)}</span>
                    ) : null}
                  </div>
                ) : null}
              </>
            );
          },
        };

        const yearColumn: Column<EntityPosition> = {
          key: 'year',
          header: 'Year',
          width: '7rem',
          sortValue: (e) => e.financialYear,
          render: (e) => <span className="smaller">{e.financialYear}</span>,
        };

        const columns: Array<Column<EntityPosition>> = [
          entityColumn,
          ...(showYear ? [yearColumn] : []),
          {
            key: 'income',
            header: 'Income',
            numeric: true,
            sortValue: (e) => e.incomeMinor,
            render: (e) => <Money minor={e.incomeMinor} blankIfZero />,
          },
          {
            key: 'expenditure',
            header: 'Expenditure',
            numeric: true,
            sortValue: (e) => e.expenditureMinor,
            render: (e) => <Money minor={e.expenditureMinor} blankIfZero />,
          },
          {
            key: 'surplus',
            header: 'Surplus',
            numeric: true,
            sortValue: (e) => e.surplusMinor,
            render: (e) => <Money minor={e.surplusMinor} blankIfZero />,
          },
          {
            key: 'budgetuse',
            header: 'Spend vs budget',
            width: '10rem',
            // Sorts on the ratio, so an entity with no budget falls to the end
            // rather than sorting as though it had spent nothing.
            sortValue: (e) =>
              e.budgetExpenditureMinor > 0 ? (e.expenditureMinor / e.budgetExpenditureMinor) * 100 : null,
            render: (e) => {
              const pc = e.budgetExpenditureMinor > 0 ? (e.expenditureMinor / e.budgetExpenditureMinor) * 100 : 0;
              return e.budgetExpenditureMinor > 0 ? (
                <>
                  <Progress percent={pc} label={`${e.entityName} spend against budget`} />
                  <span className="smaller muted">
                    {percent(pc)}
                    {e.budgetVersion ? ` of ${e.budgetVersion}` : ''}
                  </span>
                </>
              ) : (
                <span className="muted smaller">No budget set</span>
              );
            },
          },
          {
            key: 'committed',
            header: 'Committed',
            numeric: true,
            sortValue: (e) => e.commitmentMinor,
            render: (e) => <Money minor={e.commitmentMinor} blankIfZero />,
          },
          {
            key: 'creditors',
            header: 'Owed out',
            numeric: true,
            sortValue: (e) => e.creditorsMinor,
            render: (e) => <Money minor={e.creditorsMinor} blankIfZero />,
          },
          {
            key: 'debtors',
            header: 'Owed in',
            numeric: true,
            sortValue: (e) => e.debtorsMinor,
            render: (e) => <Money minor={e.debtorsMinor} blankIfZero />,
          },
          {
            key: 'cash',
            header: 'Cash',
            numeric: true,
            sortValue: (e) => e.cashMinor,
            render: (e) => <Money minor={e.cashMinor} blankIfZero />,
          },
        ];

        return (
          <div className="stack">
            {/*
              The basis sits above the figures, not below them. A reader who
              sees the bold total first and the qualification last has already
              formed the wrong impression by the time they reach it.
            */}
            <Alert tone="info" title="Basis of preparation">
              <p>{view.basis}</p>
            </Alert>

            <div className="grid grid--4">
              <Stat
                label={`${t.consolidated} surplus`}
                value={money(view.consolidated.surplusMinor)}
                tone={view.consolidated.surplusMinor >= 0 ? 'positive' : 'negative'}
                detail={hasGroupBudget ? `Budget ${money(view.consolidated.budgetSurplusMinor)}` : 'No budget set'}
              />
              <Stat
                label="Income to date"
                value={money(view.consolidated.incomeMinor)}
                detail={hasGroupBudget ? `Budget ${money(view.consolidated.budgetIncomeMinor)}` : 'No budget set'}
              />
              <Stat
                label="Expenditure to date"
                value={money(view.consolidated.expenditureMinor)}
                detail={
                  // A full-width empty bar reading "0.0% of budget" says nothing
                  // has been spent against a budget. Where no entity holds an
                  // approved budget, the truth is that there is no budget.
                  view.consolidated.budgetExpenditureMinor > 0 ? (
                    <>
                      <Progress percent={spendAgainstBudget} label={`${t.consolidated} expenditure against budget`} />
                      {percent(spendAgainstBudget)} of budget
                    </>
                  ) : (
                    'No budget set'
                  )
                }
              />
              <Stat
                label={`${t.consolidated} cash`}
                value={money(view.consolidated.cashMinor)}
                detail={`Owed to ${lower(t.supplierPlural)} ${money(view.consolidated.creditorsMinor)}`}
              />
            </div>

            <ErrorNotice error={move.error} />

            <Card
              title={`${t.entityPlural} compared`}
              subtitle={`${view.consolidated.entityCount} consolidating${
                view.consolidated.excludedCount > 0
                  ? `, ${view.consolidated.excludedCount} excluded from the total`
                  : ''
              }${yearNote}`}
              flush
            >
              <DataTable
                caption={`Financial position of each ${lower(t.entity)}`}
                rows={view.entities}
                rowKey={(e) => e.entityId}
                emptyMessage={`No ${lower(t.entityPlural)} have been set up yet.`}
                columns={columns}
                footer={
                  <tr className="is-total">
                    <td>{t.consolidated} total</td>
                    {showYear ? <td /> : null}
                    <td className="num">{money(view.consolidated.incomeMinor)}</td>
                    <td className="num">{money(view.consolidated.expenditureMinor)}</td>
                    <td className="num">{money(view.consolidated.surplusMinor)}</td>
                    <td />
                    <td className="num">{money(view.consolidated.commitmentMinor)}</td>
                    <td className="num">{money(view.consolidated.creditorsMinor)}</td>
                    <td className="num">{money(view.consolidated.debtorsMinor)}</td>
                    <td className="num">{money(view.consolidated.cashMinor)}</td>
                  </tr>
                }
              />
            </Card>

            <p className="smaller muted">
              Every headline figure above is the sum of the column beneath it. Choosing a {lower(t.entity)} moves you
              into it and takes you to your dashboard — the rest of the application follows, and your permissions are
              recalculated for the {lower(t.entity)} you are working in.
            </p>
          </div>
        );
      }}
    </AsyncPanel>
  );
}
